// Lightweight REST client for auth-related endpoints
// Expects VITE_API_BASE_URL to be defined in environment, e.g., https://api.example.com

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL;

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

export const authApi = {
  // Signup REST call
  async signUp({ company_name, first_name, last_name, email, password, role = 'user' }) {
    if (!API_BASE_URL) {
      return {
        data: null,
        error: { message: 'Missing VITE_API_BASE_URL. Please set it in your environment.' }
      };
    }

    try {
      const res = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ company_name, first_name, last_name, email, password, role })
      });

      const body = await parseJsonSafe(res);

      if (!res.ok) {
        return {
          data: null,
          error: { message: body?.error || body?.message || `Signup failed (${res.status})` }
        };
      }

      // Check the Success property in the response body
      if (body && body.Success === false) {
        return {
          data: null,
          error: { message: body?.Error || 'Signup failed' }
        };
      }

      return { data: body ?? { success: true }, error: null };
    } catch (e) {
      return {
        data: null,
        error: { message: 'Network error. Please check your connection.' }
      };
    }
  },
  // Get current user's profile (profile-centric endpoint)
  async getMyProfile({ token }) {
    if (!token) {
      return { data: null, error: { message: 'Missing auth token' } };
    }

    try {
      const base = API_BASE_URL || ''
      const res = await fetch(`${base}/users/me`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const body = await parseJsonSafe(res);

      if (!res.ok) {
        return {
          data: null,
          error: { message: body?.error || body?.message || `Fetch current profile failed (${res.status})` }
        };
      }

      return { data: body, error: null };
    } catch (e) {
      return {
        data: null,
        error: { message: 'Network error. Please check your connection.' }
      };
    }
  },
  // Get current user
  async me({ token }) {
    if (!API_BASE_URL) {
      return {
        data: null,
        error: { message: 'Missing VITE_API_BASE_URL. Please set it in your environment.' }
      };
    }

    if (!token) {
      return { data: null, error: { message: 'Missing auth token' } };
    }

    try {
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const body = await parseJsonSafe(res);

      if (!res.ok) {
        return {
          data: null,
          error: { message: body?.error || body?.message || `Fetch current user failed (${res.status})` }
        };
      }

      return { data: body, error: null };
    } catch (e) {
      return {
        data: null,
        error: { message: 'Network error. Please check your connection.' }
      };
    }
  },
  // Update current user profile
  async updateMe({ token, payload }) {
    if (!token) {
      return { data: null, error: { message: 'Missing auth token' } };
    }

    try {
      const base = API_BASE_URL || ''
      const normalizedPayload = {
        ...(payload || {}),
        phone_number:
          (payload && Object.prototype.hasOwnProperty.call(payload, 'phone_number'))
            ? (payload.phone_number ?? '')
            : ''
      };

      const res = await fetch(`${base}/users/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(normalizedPayload)
      });

      const body = await parseJsonSafe(res);

      if (!res.ok) {
        return {
          data: null,
          error: { message: body?.error || body?.message || `Update current user failed (${res.status})` }
        };
      }

      return { data: body, error: null };
    } catch (e) {
      return {
        data: null,
        error: { message: 'Network error. Please check your connection.' }
      };
    }
  },
  async sendOTP({ token, channel, destination }) {
    if (!API_BASE_URL) {
      return {
        data: null,
        error: { message: 'Missing VITE_API_BASE_URL. Please set it in your environment.' }
      };
    }

    if (!token) {
      return { data: null, error: { message: 'Missing auth token' } };
    }

    try {
      const res = await fetch(`${API_BASE_URL}/auth/otp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ channel, destination })
      });

      const body = await parseJsonSafe(res);

      if (!res.ok) {
        const retryAfterHeader = res.headers?.get('Retry-After');
        const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : (body?.retry_after_seconds ?? undefined);
        return {
          data: null,
          error: {
            status: res.status,
            retry_after_seconds: Number.isFinite(retryAfter) ? retryAfter : undefined,
            message: body?.error || body?.message || `Send OTP failed (${res.status})`
          }
        };
      }

      return { data: body ?? { status: 'sent', expires_in_seconds: 300 }, error: null };
    } catch (e) {
      return { data: null, error: { message: 'Network error. Please check your connection.' } };
    }
  },
  async verifyOTP({ token, channel, destination, code }) {
    if (!API_BASE_URL) {
      return {
        data: null,
        error: { message: 'Missing VITE_API_BASE_URL. Please set it in your environment.' }
      };
    }

    if (!token) {
      return { data: null, error: { message: 'Missing auth token' } };
    }

    try {
      const res = await fetch(`${API_BASE_URL}/auth/otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ channel, destination, code })
      });

      const body = await parseJsonSafe(res);

      if (!res.ok) {
        return {
          data: null,
          error: { message: body?.error || body?.message || `Verify OTP failed (${res.status})` }
        };
      }

      return { data: body ?? { status: 'verified' }, error: null };
    } catch (e) {
      return { data: null, error: { message: 'Network error. Please check your connection.' } };
    }
  },
  // Login REST call
  async signIn({ email, password }) {
    if (!API_BASE_URL) {
      return {
        data: null,
        error: { message: 'Missing VITE_API_BASE_URL. Please set it in your environment.' }
      };
    }

    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const body = await parseJsonSafe(res);

     
      if (!res.ok) {
        return {
          data: null,
          error: { message: body?.error || body?.message || `Login failed (${res.status})` }
        };
      }
      console.log('Login response:', res.ok);
      return { data: body, error: null };
    } catch (e) {
      return {
        data: null,
        error: { message: 'Network error. Please check your connection.' }
      };
    }
  }
};

export default authApi;
